import type { ForecastDay, WeatherCondition } from '../../lib/weather';

// Small high-temp + condition pill that floats above a day column on
// a heatmap or forecast card. Originally lived on the Revenue chart
// (commit history). Moved to the Capacity tile because weather is a
// CAPACITY / PLANNING signal — barbers care about "rainy day = lighter
// walk-ins" when scheduling, not when reviewing revenue retrospectively.
//
// Shared component so the Week At A Glance heatmap (past weather)
// + the new 7-day forecast section (forecast weather) render the same
// pill vocabulary.

// Condition → text color class. Matches the demo's `.bar-col-wx`
// vocabulary. Severe-weather (storm + heavy rain) gets a pulsing red
// dot in addition to the text color.
const CHIP_COLOR: Record<WeatherCondition, string> = {
  sunny:           'text-amber-500',
  'partly-cloudy': 'text-blue-400',
  cloudy:          'text-slate-400 dark:text-slate-300',
  fog:             'text-slate-400 dark:text-slate-300',
  rainy:           'text-blue-500',
  storm:           'text-red-600',
  snow:            'text-blue-400 dark:text-blue-300',
};

export function WeatherChip({
  day,
  size = 'md',
}: {
  day: ForecastDay;
  /** sm = compact (heatmap row labels)
   *  md = default (forecast cards, week weather strip)
   *  lg = headline (today's conditions callout — pairs with a small
   *       interpretation line beside it) */
  size?: 'sm' | 'md' | 'lg';
}) {
  const textClass =
    size === 'sm' ? 'text-[9px]'
    : size === 'lg' ? 'text-2xl'
    : 'text-[10px]';
  const iconClass =
    size === 'sm' ? 'w-2.5 h-2.5'
    : size === 'lg' ? 'w-6 h-6'
    : 'w-3 h-3';
  const dotClass =
    size === 'sm' ? 'w-1 h-1'
    : size === 'lg' ? 'w-2 h-2'
    : 'w-1.5 h-1.5';
  // Larger sizes get more breathing room between the temperature and
  // the condition icon — the gap-0.5 default reads cramped at lg.
  const gapClass = size === 'lg' ? 'gap-1.5' : 'gap-0.5';
  return (
    <span
      className={`relative inline-flex items-center ${gapClass} ${textClass} font-extrabold tabular-nums leading-none whitespace-nowrap ${CHIP_COLOR[day.condition]}`}
      title={`${day.highF}° · ${day.lowF}°`}
    >
      <span>{day.highF}°</span>
      <WeatherIcon condition={day.condition} className={iconClass} />
      {/* Pulsing red dot on severe-weather days (storms, heavy rain,
          heavy snow). Same dot the chart legend swatch references. */}
      {day.alert && (
        <span
          aria-hidden
          className={`ml-0.5 inline-block rounded-full bg-red-600 animate-pulse ${dotClass}`}
        />
      )}
    </span>
  );
}

function WeatherIcon({
  condition,
  className,
}: {
  condition: WeatherCondition;
  className: string;
}) {
  switch (condition) {
    case 'sunny':         return <SunIcon className={className} />;
    case 'partly-cloudy': return <CloudSunIcon className={className} />;
    case 'cloudy':        return <CloudIcon className={className} />;
    case 'fog':           return <CloudIcon className={className} />;
    case 'rainy':         return <RainIcon className={className} />;
    case 'storm':         return <StormIcon className={className} />;
    case 'snow':          return <SnowIcon className={className} />;
  }
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
      <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
      <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
    </svg>
  );
}
function CloudIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 19a4.5 4.5 0 1 0-1.41-8.78 6 6 0 1 0-11.07 3.78A4 4 0 0 0 6 19h11.5z" />
    </svg>
  );
}
function CloudSunIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2v2M4.93 4.93l1.41 1.41M2 12h2M5.64 12.5a4 4 0 1 1 7.16-3" />
      <path d="M17 17.5a3.5 3.5 0 1 0-1.1-6.83 4.5 4.5 0 0 0-8.4 2.83A3 3 0 0 0 9 19h7.5a3 3 0 0 0 .5-1.5z" />
    </svg>
  );
}
function RainIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 12a4.5 4.5 0 1 0-1.41-8.78 6 6 0 1 0-11.07 3.78A4 4 0 0 0 6 12h11.5z" />
      <line x1="8" y1="16" x2="8" y2="20" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="16" y1="16" x2="16" y2="20" />
    </svg>
  );
}
function StormIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 12a4.5 4.5 0 1 0-1.41-8.78 6 6 0 1 0-11.07 3.78A4 4 0 0 0 6 12h11.5z" />
      <polyline points="13 14 10 18 14 18 11 22" />
    </svg>
  );
}
function SnowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 12a4.5 4.5 0 1 0-1.41-8.78 6 6 0 1 0-11.07 3.78A4 4 0 0 0 6 12h11.5z" />
      <circle cx="8" cy="17" r="0.6" fill="currentColor" />
      <circle cx="12" cy="19" r="0.6" fill="currentColor" />
      <circle cx="16" cy="17" r="0.6" fill="currentColor" />
    </svg>
  );
}
